import React from 'react';
import {ToolName} from '../../tools/tool-name';
import {FilterNav} from '../../tools/filter/ui/filter-nav';
import {ResizeNav} from '../../tools/resize/ui/resize-nav';
import {CropNav} from '../../tools/crop/ui/crop-nav/crop-nav';
import {ShapeNav} from '../../tools/shapes/ui/shape-nav';
import {StickerNav} from '../../tools/shapes/ui/sticker-nav/sticker-nav';
import {FrameNav} from '../../tools/frame/ui/frame-nav';
import {TextNav} from '../../tools/text/ui/text-nav';
import {DrawNav} from '../../tools/draw/ui/draw-nav';
import {CornersNav} from '../../tools/corners/ui/corners-nav';
import {TransformNav} from '../../tools/transform/ui/transform-nav';
import {useStore} from '../../state/store';
import {IconButton} from '@common/ui/buttons/icon-button';
import {Button} from '@common/ui/buttons/button';
import {CloseIcon} from '@common/icons/material/Close';
import {CheckIcon} from '@common/icons/material/Check';
import {useEditorMode} from '../editor-mode';
import {Trans} from '@common/i18n/trans';
import {state} from '../../state/utils';

type Props = {
  activeTool: ToolName | null;
};

export function ToolControls({activeTool}: Props) {
  const toolNav = getToolNav(activeTool);

  return (
    <div className="relative h-full w-full text-sm bg select-none overflow-hidden flex items-center justify-between px-8 py-10 gap-x-10">
      <CancelButton />
      {toolNav}
      <ApplyButton />
    </div>
  );
}

function CancelButton() {
  const {isMobile} = useEditorMode();
  const isDirty = useStore(s => s.dirty);
  const activeTool = useStore(s => s.activeTool);

  const handleClick = () => {
    // Sticker tool için özel davranış
    if (activeTool === ToolName.STICKERS && typeof window !== 'undefined') {
      const stickerHandlers = (window as any).stickerNavHandlers;
      if (stickerHandlers?.close) {
        stickerHandlers.close();
        return;
      }
    }
    // Filter tool için özel davranış
    if (activeTool === ToolName.FILTER && typeof window !== 'undefined') {
      const filterHandlers = (window as any).filterNavHandlers;
      if (filterHandlers?.close) {
        filterHandlers.close();
        return;
      }
    }
    // Diğer tool'lar için normal davranış
    state().cancelChanges();
  };

  if (isMobile) {
    return (
      <IconButton
        size="sm"
        onClick={handleClick}
      >
        <CloseIcon />
      </IconButton>
    );
  }
  return (
    <Button
      variant="outline"
      size="xs"
      startIcon={<CloseIcon />}
      radius="rounded-none"
      className="min-w-92"
      onClick={handleClick}
    >
      {isDirty ? <Trans message="Cancel" /> : <Trans message="Close" />}
    </Button>
  );
}

function ApplyButton() {
  const {isMobile} = useEditorMode();
  const isDirty = useStore(s => s.dirty);
  const activeTool = useStore(s => s.activeTool);

  const handleClick = () => {
    // Sticker tool için özel davranış
    if (activeTool === ToolName.STICKERS && typeof window !== 'undefined') {
      const stickerHandlers = (window as any).stickerNavHandlers;
      if (stickerHandlers?.apply) {
        stickerHandlers.apply();
        return;
      }
    }
    // Filter tool için özel davranış
    if (activeTool === ToolName.FILTER && typeof window !== 'undefined') {
      const filterHandlers = (window as any).filterNavHandlers;
      if (filterHandlers?.apply) {
        filterHandlers.apply();
        return;
      }
    }
    // Diğer tool'lar için normal davranış
    state().applyChanges();
  };

  if (isMobile) {
    return (
      <IconButton
        size="sm"
        disabled={!isDirty}
        onClick={handleClick}
      >
        <CheckIcon />
      </IconButton>
    );
  }
  return (
    <Button
      variant="flat"
      color="primary"
      size="xs"
      disabled={!isDirty}
      startIcon={<CheckIcon />}
      radius="rounded-none"
      onClick={handleClick}
    >
      <Trans message="Apply" />
    </Button>
  );
}

function getToolNav(activeTool: ToolName | null) {
  switch (activeTool) {
    case ToolName.FILTER:
      return <FilterNav />;
    case ToolName.RESIZE:
      return <ResizeNav />;
    case ToolName.CROP:
      return <CropNav />;
    case ToolName.TRANSFORM:
      return <TransformNav />;
    case ToolName.DRAW:
      return <DrawNav />;
    case ToolName.TEXT:
      return <TextNav />;
    case ToolName.SHAPES:
      return <ShapeNav />;
    case ToolName.STICKERS:
      return <StickerNav />;
    case ToolName.FRAME:
      return <FrameNav />;
    case ToolName.CORNERS:
      return <CornersNav />;
    default:
      return null;
  }
}
